import { pathfinder } from '@blade/socket-types'
import { Result } from '@blade/std-lib'

import type { QuoteResponseBody } from '@blade/pathfinder-api-client'

const {
  PATHFINDER_WS_PATH,
  PathfinderWsMethod,
  PathfinderWsPushMethod,
  PathfinderWsErrorCode,
  isPathfinderWsQuoteSuccess,
  isPathfinderWsQuoteError,
  serializePathfinderWsRoomKey,
} = pathfinder

type SubscribeParameters = pathfinder.PathfinderWsSubscribeParameters
type RoomKey = pathfinder.PathfinderWsRoomKey
type QuoteNotification = pathfinder.PathfinderWsQuoteNotification
type RpcId = pathfinder.PathfinderWsRpcId

export type PathfinderWsConnectionState =
  | { kind: 'idle' }
  | { kind: 'connecting' }
  | { kind: 'open' }
  | { kind: 'reconnecting'; attempt: number; nextDelayMs: number }
  | { kind: 'disposed' }

export interface PathfinderWsClientConfig {
  wsUrl: string
  /** Override global WebSocket (tests). */
  webSocketImpl?: typeof WebSocket
  /** Client-side keepalive ping interval ms. 0 disables. Default 25_000. */
  keepaliveIntervalMs?: number
  /** Keepalive ping response timeout ms; if exceeded, force-reconnect. Default 5_000. */
  keepaliveTimeoutMs?: number
  /** Initial reconnect backoff ms. Default 500. */
  reconnectInitialDelayMs?: number
  /** Max reconnect backoff ms. Default 30_000. */
  reconnectMaxDelayMs?: number
  /** Request response timeout ms. Default 10_000. */
  requestTimeoutMs?: number
  /** Grace period before unsubscribing a room with no listeners; deduplicates churn. Default 250. */
  unsubscribeGraceMs?: number
  /**
   * Client-side auto-refresh: if a room hasn't received an update within this window,
   * force unsubscribe + resubscribe to get a fresh blockhash-bound tx.
   * Workaround until server's ws_transaction_refresh_interval_ms ships.
   * Default 45_000. 0 disables.
   */
  autoRefreshMs?: number
  /**
   * HTTP base URL (e.g. `https://test-pathfinder.di.cash`) used as initial-quote
   * fallback when the server doesn't push a quote shortly after subscribe-ack.
   * Empty/undefined disables fallback.
   */
  httpFallbackUrl?: string
  /**
   * Delay after subscribe-ack before firing the HTTP-fallback initial-quote request.
   * If a push arrives within this window, the fallback is cancelled. Default 1_000. 0 disables.
   */
  httpFallbackDelayMs?: number
  /** Override fetch (tests). */
  fetchImpl?: typeof fetch
}

export interface PathfinderQuoteListener {
  onUpdate: (quote: QuoteResponseBody, room: RoomKey) => void
  onError: (message: string, room: RoomKey) => void
}

export interface PathfinderQuoteSubscription {
  readonly room: RoomKey
  unsubscribe: () => void
}

export class PathfinderWsError extends Error {
  constructor(
    message: string,
    public readonly code?: number,
    public readonly cause?: unknown,
  ) {
    super(message)
    this.name = 'PathfinderWsError'
  }
}

export interface PathfinderWsClient {
  readonly state: PathfinderWsConnectionState
  onStateChange: (
    listener: (state: PathfinderWsConnectionState) => void,
  ) => () => void
  subscribeQuote: (
    params: SubscribeParameters,
    listener: PathfinderQuoteListener,
  ) => Promise<Result.Result<PathfinderQuoteSubscription, PathfinderWsError>>
  ping: () => Promise<Result.Result<number, PathfinderWsError>>
  dispose: () => void
  openSocket: () => void
}

export const deriveDefaultPathfinderWsUrl = (httpBaseUrl: string): string => {
  const trimmed = httpBaseUrl.replace(/\/$/, '')
  if (trimmed.length === 0) {
    return ''
  }
  const wsBase = trimmed.replace(/^http/, 'ws')
  return `${wsBase}${PATHFINDER_WS_PATH}`
}

type RoomEntry = {
  room: RoomKey
  params: SubscribeParameters
  listeners: Set<PathfinderQuoteListener>
  subscriptionId?: number
  unsubscribeTimer?: number
  autoRefreshTimer?: number
  lastUpdateAt?: number
  httpFallbackTimer?: number
  httpFallbackAbort?: AbortController
}

type PendingRequest = {
  resolve: (value: unknown) => void
  reject: (error: PathfinderWsError) => void
  method: string
  timer: number
}

export const createPathfinderWsClient = (
  config: PathfinderWsClientConfig,
): PathfinderWsClient => {
  if (!config.wsUrl) {
    throw new PathfinderWsError('PathfinderWsClient: wsUrl is required')
  }
  const WebSocketImpl: typeof WebSocket | undefined =
    config.webSocketImpl ??
    (globalThis as { WebSocket?: typeof WebSocket }).WebSocket
  if (!WebSocketImpl) {
    throw new PathfinderWsError(
      'PathfinderWsClient: WebSocket not available in this environment',
    )
  }

  const keepaliveIntervalMs = config.keepaliveIntervalMs ?? 25_000
  const keepaliveTimeoutMs = config.keepaliveTimeoutMs ?? 5000
  const reconnectInitialDelayMs = config.reconnectInitialDelayMs ?? 500
  const reconnectMaxDelayMs = config.reconnectMaxDelayMs ?? 30_000
  const requestTimeoutMs = config.requestTimeoutMs ?? 10_000
  const unsubscribeGraceMs = config.unsubscribeGraceMs ?? 250
  const autoRefreshMs = config.autoRefreshMs ?? 45_000
  const httpFallbackUrl = (config.httpFallbackUrl ?? '').replace(/\/$/, '')
  const httpFallbackDelayMs = config.httpFallbackDelayMs ?? 1000
  const fetchImpl: typeof fetch | undefined =
    config.fetchImpl ?? (globalThis as { fetch?: typeof fetch }).fetch

  let socket: WebSocket | undefined
  let state: PathfinderWsConnectionState = { kind: 'idle' }
  const stateListeners = new Set<(state: PathfinderWsConnectionState) => void>()

  let nextRpcId = 1
  const pending = new Map<RpcId, PendingRequest>()

  const rooms = new Map<string, RoomEntry>()

  let outboundQueue: Array<{ payload: object }> = []
  let flushScheduled = false

  let reconnectAttempt = 0
  let reconnectTimer: number | undefined
  let keepaliveTimer: number | undefined
  let keepaliveTimeoutTimer: number | undefined

  const scheduleTimer = (fn: () => void, ms: number): number =>
    setTimeout(fn, ms) as unknown as number
  const scheduleInterval = (fn: () => void, ms: number): number =>
    setInterval(fn, ms) as unknown as number

  const setState = (next: PathfinderWsConnectionState) => {
    state = next
    for (const listener of stateListeners) {
      try {
        listener(state)
      } catch {
        // listener errors are isolated
      }
    }
  }

  const cancelReconnectTimer = () => {
    if (reconnectTimer !== undefined) {
      clearTimeout(reconnectTimer)
      reconnectTimer = undefined
    }
  }

  const stopKeepalive = () => {
    if (keepaliveTimer !== undefined) {
      clearInterval(keepaliveTimer)
      keepaliveTimer = undefined
    }
    if (keepaliveTimeoutTimer !== undefined) {
      clearTimeout(keepaliveTimeoutTimer)
      keepaliveTimeoutTimer = undefined
    }
  }

  const startKeepalive = () => {
    if (keepaliveIntervalMs <= 0) {
      return
    }
    stopKeepalive()
    keepaliveTimer = scheduleInterval(() => {
      sendPing()
        .then((rttMsResult) => {
          if (Result.isFailure(rttMsResult)) {
            forceReconnect('keepalive-failed')
          }
        })
        .catch(() => forceReconnect('keepalive-threw'))
    }, keepaliveIntervalMs)
  }

  const computeBackoffMs = (attempt: number): number => {
    const exp = reconnectInitialDelayMs * 2 ** Math.max(0, attempt - 1)
    return Math.min(exp, reconnectMaxDelayMs)
  }

  const scheduleReconnect = () => {
    if (state.kind === 'disposed') {
      return
    }
    cancelReconnectTimer()
    reconnectAttempt += 1
    const delayMs = computeBackoffMs(reconnectAttempt)
    setState({
      kind: 'reconnecting',
      attempt: reconnectAttempt,
      nextDelayMs: delayMs,
    })
    reconnectTimer = scheduleTimer(() => {
      reconnectTimer = undefined
      openSocket()
    }, delayMs)
  }

  const failPending = (reason: string) => {
    const err = new PathfinderWsError(`pathfinder ws ${reason}`)
    for (const [, req] of pending) {
      clearTimeout(req.timer)
      req.reject(err)
    }
    pending.clear()
  }

  const forceReconnect = (reason: string) => {
    if (state.kind === 'disposed') {
      return
    }
    stopKeepalive()
    failPending(reason)
    if (socket) {
      try {
        socket.close(4000, reason)
      } catch {
        // ignore
      }
    }
  }

  const cancelAutoRefresh = (entry: RoomEntry) => {
    if (entry.autoRefreshTimer !== undefined) {
      clearTimeout(entry.autoRefreshTimer)
      entry.autoRefreshTimer = undefined
    }
  }

  const cancelHttpFallback = (entry: RoomEntry) => {
    if (entry.httpFallbackTimer !== undefined) {
      clearTimeout(entry.httpFallbackTimer)
      entry.httpFallbackTimer = undefined
    }
    if (entry.httpFallbackAbort !== undefined) {
      try {
        entry.httpFallbackAbort.abort()
      } catch {
        // ignore
      }
      entry.httpFallbackAbort = undefined
    }
  }

  const buildHttpFallbackBody = (
    params: SubscribeParameters,
  ): Record<string, unknown> => {
    const body: Record<string, unknown> = {
      token_in: params.token_in,
      token_out: params.token_out,
      amount_in: params.amount,
      allow_split: params.allow_split,
    }
    if (params.taker) {
      body.taker = params.taker
    }
    if (params.referral_code) {
      body.referral_code = params.referral_code
    }
    if (params.slippage !== undefined) {
      body.slippage = params.slippage
    }
    if (params.priority !== undefined) {
      body.priority = params.priority
    }
    if (params.tip !== undefined) {
      body.tip = params.tip
    }
    if (params.compute_unit !== undefined) {
      body.compute_unit = params.compute_unit
    }
    if (params.sender_type) {
      body.sender_type = params.sender_type
    }
    if (params.use_alt !== undefined) {
      body.use_alt = params.use_alt
    }
    return body
  }

  const notifyListenersUpdate = (
    entry: RoomEntry,
    quote: QuoteResponseBody,
  ) => {
    for (const listener of entry.listeners) {
      try {
        listener.onUpdate(quote, entry.room)
      } catch {
        // isolated
      }
    }
  }

  const notifyListenersError = (entry: RoomEntry, message: string) => {
    for (const listener of entry.listeners) {
      try {
        listener.onError(message, entry.room)
      } catch {
        // isolated
      }
    }
  }

  const isStale = (entry: RoomEntry, abort: AbortController): boolean => {
    if (abort.signal.aborted) {
      return true
    }
    return entry.lastUpdateAt !== undefined
  }

  const fireHttpFallback = async (entry: RoomEntry) => {
    if (!httpFallbackUrl || !fetchImpl) {
      return
    }
    if (entry.lastUpdateAt !== undefined) {
      return
    }
    if (entry.listeners.size === 0) {
      return
    }
    const abort = new AbortController()
    entry.httpFallbackAbort = abort
    const body = buildHttpFallbackBody(entry.params)
    try {
      const response = await fetchImpl(`${httpFallbackUrl}/v1/quote`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
        signal: abort.signal,
      })
      if (isStale(entry, abort)) {
        return
      }
      if (!response.ok) {
        const text = await response.text().catch(() => '')
        notifyListenersError(
          entry,
          `http-fallback /v1/quote ${response.status}: ${text || response.statusText}`,
        )
        return
      }
      const quote = (await response.json()) as QuoteResponseBody
      if (isStale(entry, abort)) {
        return
      }
      entry.lastUpdateAt = Date.now()
      armAutoRefresh(entry)
      notifyListenersUpdate(entry, quote)
    } catch (error) {
      if (abort.signal.aborted) {
        return
      }
      notifyListenersError(
        entry,
        error instanceof Error
          ? `http-fallback /v1/quote: ${error.message}`
          : 'http-fallback /v1/quote failed',
      )
    } finally {
      if (entry.httpFallbackAbort === abort) {
        entry.httpFallbackAbort = undefined
      }
    }
  }

  const armHttpFallback = (entry: RoomEntry) => {
    cancelHttpFallback(entry)
    if (!httpFallbackUrl || httpFallbackDelayMs <= 0 || !fetchImpl) {
      return
    }
    if (entry.lastUpdateAt !== undefined) {
      return
    }
    entry.httpFallbackTimer = scheduleTimer(() => {
      entry.httpFallbackTimer = undefined
      void fireHttpFallback(entry)
    }, httpFallbackDelayMs)
  }

  const armAutoRefresh = (entry: RoomEntry) => {
    cancelAutoRefresh(entry)
    if (autoRefreshMs <= 0) {
      return
    }
    entry.autoRefreshTimer = scheduleTimer(() => {
      entry.autoRefreshTimer = undefined
      forceRefreshRoom(entry)
    }, autoRefreshMs)
  }

  const forceRefreshRoom = (entry: RoomEntry) => {
    if (entry.listeners.size === 0) {
      return
    }
    if (state.kind !== 'open') {
      return
    }
    cancelHttpFallback(entry)
    entry.lastUpdateAt = undefined
    if (entry.subscriptionId !== undefined) {
      enqueueUnsubscribe(entry.subscriptionId)
      entry.subscriptionId = undefined
    }
    enqueueSubscribe(entry).catch(() => {
      // surfaced to listeners via subsequent error notification or reconnect
    })
  }

  const openSocket = () => {
    if (state.kind === 'disposed') {
      return
    }
    cancelReconnectTimer()
    setState({ kind: 'connecting' })
    try {
      socket = new WebSocketImpl(config.wsUrl)
    } catch (error) {
      scheduleReconnect()
      throw new PathfinderWsError(
        'pathfinder ws construct failed',
        undefined,
        error,
      )
    }

    socket.addEventListener('open', handleOpen)
    socket.addEventListener('message', handleMessage)
    socket.addEventListener('close', handleClose)
    socket.addEventListener('error', handleSocketError)
  }

  const waitForOpenOrTerminalState =
    (): Promise<PathfinderWsConnectionState> => {
      if (state.kind === 'open' || state.kind === 'disposed') {
        return Promise.resolve(state)
      }
      return new Promise((resolve) => {
        const unsubscribe = (() => {
          stateListeners.add(onChange)
          return () => stateListeners.delete(onChange)
        })()
        function onChange(next: PathfinderWsConnectionState) {
          if (next.kind === 'open' || next.kind === 'disposed') {
            unsubscribe()
            resolve(next)
          }
        }
      })
    }

  const handleOpen = () => {
    reconnectAttempt = 0
    setState({ kind: 'open' })
    startKeepalive()

    for (const [, entry] of rooms) {
      cancelAutoRefresh(entry)
      cancelHttpFallback(entry)
      if (entry.unsubscribeTimer !== undefined) {
        clearTimeout(entry.unsubscribeTimer)
        entry.unsubscribeTimer = undefined
      }
      if (entry.subscriptionId !== undefined && entry.listeners.size > 0) {
        entry.subscriptionId = undefined
        enqueueSubscribe(entry).catch(() => {
          // re-subscribe failure surfaces via next reconnect attempt
        })
      }
    }
  }

  const handleSocketError = () => {
    // close will follow; nothing to do directly.
  }

  const handleClose = () => {
    stopKeepalive()
    failPending('socket-closed')
    socket = undefined
    if (state.kind === 'disposed') {
      return
    }
    scheduleReconnect()
  }

  const handleMessage = (event: MessageEvent) => {
    const raw = typeof event.data === 'string' ? event.data : String(event.data)
    let parsed: unknown
    try {
      parsed = JSON.parse(raw)
    } catch {
      return
    }
    if (Array.isArray(parsed)) {
      for (const item of parsed) {
        handleSingleFrame(item)
      }
      return
    }
    handleSingleFrame(parsed)
  }

  const handleSingleFrame = (frame: unknown) => {
    if (typeof frame !== 'object' || frame === null) {
      return
    }
    const obj = frame as Record<string, unknown>
    if ('id' in obj && obj.id !== null && obj.id !== undefined) {
      handleResponse(obj as Record<string, unknown> & { id: RpcId })
      return
    }
    if (obj.method === PathfinderWsPushMethod.Quote) {
      handleQuoteNotification(frame as QuoteNotification)
    }
  }

  const handleResponse = (frame: Record<string, unknown> & { id: RpcId }) => {
    const req = pending.get(frame.id)
    if (!req) {
      return
    }
    clearTimeout(req.timer)
    pending.delete(frame.id)
    if ('error' in frame && frame.error && typeof frame.error === 'object') {
      const errObj = frame.error as { code?: number; message?: string }
      req.reject(
        new PathfinderWsError(
          errObj.message ?? 'unknown rpc error',
          errObj.code,
        ),
      )
      return
    }
    req.resolve('result' in frame ? frame.result : undefined)
  }

  const handleQuoteNotification = (notification: QuoteNotification) => {
    const { room } = notification.params
    const key = serializePathfinderWsRoomKey(room)
    const entry = rooms.get(key)
    if (!entry || entry.listeners.size === 0) {
      return
    }
    if (isPathfinderWsQuoteSuccess(notification)) {
      const quote = notification.params.result
      entry.lastUpdateAt = Date.now()
      cancelHttpFallback(entry)
      armAutoRefresh(entry)
      for (const listener of entry.listeners) {
        try {
          listener.onUpdate(quote, room)
        } catch {
          // listener errors are isolated
        }
      }
      return
    }
    if (!isPathfinderWsQuoteError(notification)) {
      return
    }
    const message = notification.params.error.message
    for (const listener of entry.listeners) {
      try {
        listener.onError(message, room)
      } catch {
        // listener errors are isolated
      }
    }
  }

  const ensureFlushScheduled = () => {
    if (flushScheduled) {
      return
    }
    flushScheduled = true
    queueMicrotask(flushOutbound)
  }

  const flushOutbound = () => {
    flushScheduled = false
    if (!socket || socket.readyState !== WebSocketImpl.OPEN) {
      return
    }
    if (outboundQueue.length === 0) {
      return
    }
    const batch = outboundQueue
    outboundQueue = []
    const payload =
      batch.length === 1
        ? JSON.stringify(batch[0]!.payload)
        : JSON.stringify(batch.map((item) => item.payload))
    socket.send(payload)
  }

  const sendRequest = <T>(method: string, params?: object): Promise<T> => {
    return new Promise((resolve, reject) => {
      const id = nextRpcId++
      const timer = scheduleTimer(() => {
        pending.delete(id)
        reject(new PathfinderWsError(`pathfinder ws ${method} timeout`))
      }, requestTimeoutMs)
      pending.set(id, {
        resolve: resolve as (value: unknown) => void,
        reject,
        method,
        timer,
      })
      const payload: Record<string, unknown> = {
        jsonrpc: '2.0',
        id,
        method,
      }
      if (params !== undefined) {
        payload.params = params
      }
      outboundQueue.push({ payload })
      if (state.kind === 'open') {
        ensureFlushScheduled()
      }
    })
  }

  const sendNotification = (method: string, params?: object) => {
    const payload: Record<string, unknown> = { jsonrpc: '2.0', method }
    if (params !== undefined) {
      payload.params = params
    }
    outboundQueue.push({ payload })
    if (state.kind === 'open') {
      ensureFlushScheduled()
    }
  }

  const sendPing = async (): Promise<
    Result.Result<number, PathfinderWsError>
  > => {
    if (state.kind !== 'open') {
      return Result.fail(new PathfinderWsError('ping requires open connection'))
    }
    const t0 =
      typeof performance === 'undefined' ? Date.now() : performance.now()
    const timeout = new Promise<'timeout'>((resolve) => {
      keepaliveTimeoutTimer = scheduleTimer(
        () => resolve('timeout'),
        keepaliveTimeoutMs,
      )
    })
    try {
      const winner = await Promise.race([
        sendRequest<string>(PathfinderWsMethod.Ping).then((r) => ({ ok: r })),
        timeout,
      ])
      if (winner === 'timeout') {
        return Result.fail(new PathfinderWsError('ping timeout'))
      }
      const elapsed =
        (typeof performance === 'undefined' ? Date.now() : performance.now()) -
        t0
      return Result.succeed(elapsed)
    } catch (error) {
      return Result.fail(
        error instanceof PathfinderWsError
          ? error
          : new PathfinderWsError('ping failed', undefined, error),
      )
    } finally {
      if (keepaliveTimeoutTimer !== undefined) {
        clearTimeout(keepaliveTimeoutTimer)
        keepaliveTimeoutTimer = undefined
      }
    }
  }

  const enqueueSubscribe = (entry: RoomEntry): Promise<number> => {
    const promise = sendRequest<{ subscription_id: number }>(
      PathfinderWsMethod.Subscribe,
      entry.params,
    ).then((res) => {
      entry.subscriptionId = res.subscription_id
      armAutoRefresh(entry)
      armHttpFallback(entry)
      return res.subscription_id
    })
    return promise
  }

  const enqueueUnsubscribe = (subscriptionId: number) => {
    sendNotification(PathfinderWsMethod.Unsubscribe, {
      subscription_id: subscriptionId,
    })
  }

  const subscribeQuote: PathfinderWsClient['subscribeQuote'] = async (
    params,
    listener,
  ) => {
    if (state.kind === 'idle') {
      openSocket()
    }
    if (state.kind === 'disposed') {
      return Result.fail(new PathfinderWsError('client disposed'))
    }

    const room: RoomKey = {
      token_in: params.token_in,
      token_out: params.token_out,
      amount: params.amount,
      allow_split: params.allow_split,
    }
    const key = serializePathfinderWsRoomKey(room)
    let entry = rooms.get(key)

    if (entry) {
      if (entry.unsubscribeTimer !== undefined) {
        clearTimeout(entry.unsubscribeTimer)
        entry.unsubscribeTimer = undefined
      }
      entry.listeners.add(listener)
    } else {
      entry = { room, params, listeners: new Set([listener]) }
      rooms.set(key, entry)
      if (state.kind !== 'open') {
        const next = await waitForOpenOrTerminalState()
        if (next.kind === 'disposed') {
          rooms.delete(key)
          return Result.fail(new PathfinderWsError('client disposed'))
        }
      }
      try {
        await enqueueSubscribe(entry)
      } catch (error) {
        if (entry.listeners.size === 0) {
          rooms.delete(key)
        }
        return Result.fail(
          error instanceof PathfinderWsError
            ? error
            : new PathfinderWsError('subscribe failed', undefined, error),
        )
      }
    }

    const subscription: PathfinderQuoteSubscription = {
      room,
      unsubscribe: () => {
        const current = rooms.get(key)
        if (!current) {
          return
        }
        current.listeners.delete(listener)
        if (current.listeners.size > 0) {
          return
        }
        current.unsubscribeTimer = scheduleTimer(() => {
          const stillEmpty = rooms.get(key)
          if (!stillEmpty || stillEmpty.listeners.size > 0) {
            return
          }
          if (
            stillEmpty.subscriptionId !== undefined &&
            state.kind === 'open'
          ) {
            enqueueUnsubscribe(stillEmpty.subscriptionId)
          }
          cancelAutoRefresh(stillEmpty)
          cancelHttpFallback(stillEmpty)
          rooms.delete(key)
        }, unsubscribeGraceMs)
      },
    }
    return Result.succeed(subscription)
  }

  const ping: PathfinderWsClient['ping'] = async () => {
    if (state.kind === 'idle') {
      openSocket()
    }
    if (state.kind !== 'open') {
      return Result.fail(
        new PathfinderWsError(`ping: connection not open (${state.kind})`),
      )
    }
    return sendPing()
  }

  const dispose = () => {
    setState({ kind: 'disposed' })
    cancelReconnectTimer()
    stopKeepalive()
    failPending('disposed')
    for (const [, entry] of rooms) {
      if (entry.unsubscribeTimer !== undefined) {
        clearTimeout(entry.unsubscribeTimer)
      }
      cancelAutoRefresh(entry)
      cancelHttpFallback(entry)
    }
    rooms.clear()
    outboundQueue = []
    if (socket) {
      try {
        socket.close(1000, 'client-disposed')
      } catch {
        // ignore
      }
      socket = undefined
    }
  }

  return {
    get state() {
      return state
    },
    onStateChange: (listener) => {
      stateListeners.add(listener)
      return () => stateListeners.delete(listener)
    },
    openSocket,
    subscribeQuote,
    ping,
    dispose,
  }
}

export const PATHFINDER_WS_ERROR_CODE_QUOTE_NOT_FOUND =
  PathfinderWsErrorCode.QuoteNotFound
